import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { X, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { useMutation } from "convex/react";
import { api } from "@/convex/_generated/api";

interface ImageUploadSlotsProps {
  images: string[];
  onChange: (images: string[]) => void;
}

export default function ImageUploadSlots({ images, onChange }: ImageUploadSlotsProps) {
  const generateUploadUrl = useMutation((api as any).products.generateUploadUrl);
  const saveFile = useMutation((api as any).files.saveFile);

  const handleImageUpload = async (file: File, index: number) => {
    try {
      const uploadUrl = await generateUploadUrl();
      const result = await fetch(uploadUrl, {
        method: "POST",
        headers: { "Content-Type": file.type },
        body: file,
      });
      const { storageId } = await result.json();
      
      // Register file in our system
      await saveFile({
        storageId,
        name: file.name,
        type: file.type,
        size: file.size,
      });

      const newImages = [...images];
      newImages[index] = storageId;
      onChange(newImages);
      toast.success("Image uploaded successfully");
    } catch (error) {
      console.error(error);
      toast.error("Failed to upload image");
    }
  };

  const handleRemoveImage = (index: number) => {
    const newImages = [...images];
    newImages[index] = "";
    onChange(newImages);
  };

  return (
    <div className="space-y-2">
      <Label>Product Images (Up to 6)</Label>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {[0, 1, 2, 3, 4, 5].map((index) => (
          <div key={index} className="relative border-2 border-dashed rounded-lg p-4 hover:border-primary transition-colors">
            {images[index] ? (
              <div className="relative">
                <img
                  src={`${import.meta.env.VITE_CONVEX_URL}/api/storage/${images[index]}`}
                  alt={`Product ${index + 1}`}
                  className="w-full h-32 object-cover rounded"
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="absolute top-1 right-1 h-6 w-6"
                  onClick={() => handleRemoveImage(index)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center h-32 cursor-pointer">
                <ImageIcon className="h-8 w-8 text-muted-foreground mb-2" />
                <span className="text-sm text-muted-foreground">Upload Image {index + 1}</span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageUpload(file, index);
                  }}
                />
              </label>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}